export const IdentityVerificationTier = {
  UNVERIFIED: 'UNVERIFIED',
  KYC_PENDING: 'KYC_PENDING',
  KYC_FAILED: 'KYC_FAILED',
  KYC_MANUAL_REVIEW: 'KYC_MANUAL_REVIEW',
  DOCUMENT_VERIFIED: 'DOCUMENT_VERIFIED',
  GUARDIAN_ATTESTED: 'GUARDIAN_ATTESTED',
  FULLY_VERIFIED: 'FULLY_VERIFIED',
} as const;

export type IdentityVerificationTierValue =
  (typeof IdentityVerificationTier)[keyof typeof IdentityVerificationTier];

export interface IdentityVerificationStatus {
  userId: string;
  tier: string;
  verifiedAt?: number;
  activeKycSessionId?: string;
  kycProvider?: string;
  graceUntil?: number;
  inGracePeriod?: boolean;
  graceDaysRemaining?: number;
  /** Server-evaluated policy hint; prefer over client-side tier checks when present. */
  canPerformFamilyActions?: boolean;
}

export interface KycSession {
  id: string;
  userId: string;
  provider: string;
  externalCaseId?: string;
  status: string;
  captureUrl?: string;
  expiresAt?: number;
  correlationId: string;
}

export interface IdentityLayerConfig {
  layerEnabled: boolean;
}

export interface KycSimulateRequest {
  success?: boolean;
  documentCountry?: string;
  birthDate?: string;
}

export function isFullyVerifiedTier(tier: string | undefined): boolean {
  return tier === IdentityVerificationTier.FULLY_VERIFIED;
}

export function isDocumentVerifiedTier(tier: string | undefined): boolean {
  return (
    tier === IdentityVerificationTier.DOCUMENT_VERIFIED ||
    tier === IdentityVerificationTier.FULLY_VERIFIED
  );
}

export function needsIdentityVerification(tier: string | undefined): boolean {
  if (!tier || tier === IdentityVerificationTier.UNVERIFIED) {
    return true;
  }
  return !isDocumentVerifiedTier(tier);
}

export type KycMockStep =
  | 'intro'
  | 'consent'
  | 'documentType'
  | 'documentFront'
  | 'documentBack'
  | 'selfie'
  | 'processing'
  | 'result';

export const KYC_PLACEHOLDER_PROVIDER = 'regula_placeholder';

/** Must match authentication-service IDENTITY_CONSENT_TERMS_VERSION */
export const IDENTITY_CONSENT_TERMS_VERSION = '2026-07-08';

export interface KycConsentRequest {
  consentAccepted: boolean;
  termsVersion: string;
  /** I4-02 parent password step-up (flag off) */
  password?: string;
  /** I4-02 TOTP step-up (flag on) */
  totpCode?: string;
}
